import { CompanyCandidateListQuery, PlatformCompanyCandidateListOrderFields } from "@generated/graphql";
import { Chip, Link } from "@mui/material";
import DownloadReportButton from "components/buttons/DownloadReportButton";
import TestingStatus from "components/TestingStatus";
import { COMPANY_CANDIDATE_TYPE_TITLE } from "constants/global";
import dayjs from "dayjs";
import { formatPhoneNumber, gradeColor, secondsToMinutes } from "helpers/index";
import { ColumnConfig, RouteEnum } from "layout/TableConstructor/types";
import { CompanyCandidateFilter } from "pages/Candidate/list/tableConfig/filterConfig";

export type CompanyCandidateItem = NonNullable<CompanyCandidateListQuery["companyCandidates"]["data"]>[number];

const columnConfig: ColumnConfig<
  CompanyCandidateItem,
  CompanyCandidateFilter,
  PlatformCompanyCandidateListOrderFields,
  undefined,
  RouteEnum.Candidate
>[] = [
  {
    name: "numericId",
    title: "ID кандидата",
    render: (data) => data.numericId,
    filterName: "numericId",
    sortField: PlatformCompanyCandidateListOrderFields.NumericId,
  },
  {
    name: "fullName",
    title: "ФИО кандидата",
    render: (data) => data.fullName,
    filterName: "fullName",
    sortField: PlatformCompanyCandidateListOrderFields.FullName,
  },
  {
    name: "createdAt",
    title: "Дата создания",
    filterName: "createdAt",
    sortField: PlatformCompanyCandidateListOrderFields.CreatedAt,
    render: (data) => (data.createdAt ? dayjs(data.createdAt).format("DD.MM.YYYY") : "-"),
  },
  {
    name: "action",
    title: "Действие",
    render: (data) => {
      const lastTrialReports = data.lastTrialRecruiterReportFiles;
      if (!lastTrialReports) return "-";
      return <DownloadReportButton recruiterReportFiles={lastTrialReports} />;
    },
  },
  {
    name: "organization",
    title: "Организация",
    filterName: "organization",
    sortField: PlatformCompanyCandidateListOrderFields.Organization,
    render: (data) => data.organization || "-",
  },
  {
    name: "position",
    title: "Должность",
    filterName: "position",
    sortField: PlatformCompanyCandidateListOrderFields.Position,
    render: (data) => data.position || "-",
  },
  {
    name: "type",
    title: "Тип",
    filterName: "type",
    sortField: PlatformCompanyCandidateListOrderFields.Type,
    render: (data) => (data.type ? COMPANY_CANDIDATE_TYPE_TITLE[data.type] : "-"),
  },
  {
    name: "grade",
    title: "Грейд",
    filterName: "grade",
    sortField: PlatformCompanyCandidateListOrderFields.Grade,
    render: (data) =>
      data.grade ? (
        <Chip
          label={data.grade}
          sx={{ backgroundColor: `${gradeColor(data.grade)}.light`, color: `${gradeColor(data.grade)}.contrast` }}
        />
      ) : (
        "-"
      ),
  },
  {
    name: "workEmail",
    title: "Рабочий Email",
    filterName: "workEmail",
    render: (data) => data.email || "-",
  },
  {
    name: "phoneNumber",
    title: "Номер телефона",
    filterName: "phoneNumber",
    sortField: PlatformCompanyCandidateListOrderFields.CandidateUsernameCandidatePhoneNumber,
    render: (data) => formatPhoneNumber(data.candidateUsername?.candidate?.phoneNumber) || "-",
    cellStyle: { whiteSpace: "nowrap" },
  },
  {
    name: "email",
    title: "Email",
    filterName: "email",
    sortField: PlatformCompanyCandidateListOrderFields.Email,
    render: (data) => data.candidateUsername?.email || "-",
  },
  {
    name: "login",
    title: "Логин",
    filterName: "login",
    sortField: PlatformCompanyCandidateListOrderFields.CandidateUsernameLogin,
    render: (data) => data.candidateUsername?.login || "-",
  },
  {
    name: "telegramUsername",
    title: "Ник Telegram",
    filterName: "telegramUsername",
    sortField: PlatformCompanyCandidateListOrderFields.CandidateUsernameTelegramUsername,
    render: (data) => data.candidateUsername?.telegramUsername || "-",
  },
  {
    name: "yandexEmail",
    title: "Yandex Email",
    filterName: "yandexEmail",
    render: (data) => data.candidateUsername?.yandexEmail || "-",
  },
  {
    name: "vkID",
    title: "VK ID",
    filterName: "vkId",
    render: (data) => data.candidateUsername?.vkId || "-",
  },
  {
    name: "link",
    title: "Ссылка на HH",
    render: (data) =>
      data.link ? (
        <Link
          href={data.link}
          target='_blank'>
          {data.link}
        </Link>
      ) : (
        "-"
      ),
  },
  {
    name: "lastTrialDate",
    title: "Дата последнего тестирования",
    filterName: "completedAt",
    render: (data) => {
      const { lastTrialDate } = data;
      if (lastTrialDate) return dayjs(lastTrialDate).format("DD.MM.YYYY");
      return "-";
    },
  },
  {
    name: "trialsCount",
    title: "Количество пройденных тестирований",
    filterName: "trialsCount",
    render: (data) => (typeof data.trialsCount === "number" ? data.trialsCount : "-"),
  },
  {
    name: "averageTrialsScore",
    title: "Средний результат",
    filterName: "averageScore",
    render: (data) => (typeof data.averageTrialsScore === "number" ? data.averageTrialsScore.toFixed(1) : "-"),
  },
  {
    name: "lastTrialScore",
    title: "Последний результат",
    filterName: "lastTrialScore",
    render: (data) => (typeof data.lastTrialScore === "number" ? data.lastTrialScore.toFixed(1) : "-"),
  },
  {
    name: "lastTrialStatus",
    title: "Статус последнего тестирования",
    filterName: "lastTrialStatus",
    render: (data) =>
      data.lastTrialStatus ? (
        <TestingStatus
          sx={{ backgroundColor: "transparent", p: 0 }}
          status={data.lastTrialStatus}
          typographyProps={{ variant: "body14rg" }}
        />
      ) : (
        "-"
      ),
  },
  {
    name: "lastTrialTime",
    filterName: "lastTrialTime",
    title: "Время последнего тестирования",
    render: (data) => (data.lastTrialTime ? secondsToMinutes(data.lastTrialTime) : "-"),
  },
];

export default columnConfig;
